import { formatDistanceToNowStrict } from 'date-fns';
import { useGlobalSearchParams } from 'expo-router';
import { forwardRef } from 'react';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { CommentFooter } from './CommentFooter';
import { UserAvatar } from './UserAvatar';
import { UserInfo } from './UserInfo';
import {
  getCommentIsAnonymous,
  getDisplayName,
  getInitials,
  getReplyToName,
} from '../../model/authorDisplay';
import type { AnyComment } from '../../model/types';

import { ConversationEntry, ConversationText } from '@/components/ConversationEntry';
import { VStack } from '@/components/ui/vstack';

const getTimeAgo = (timestamp: string | Date): string => {
  return formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true });
};

export const Comment = forwardRef<
  View,
  {
    postId: number;
    comment: AnyComment;
    isReply?: boolean;
    onReply?: (comment: AnyComment) => void;
    commentMap?: Map<number, AnyComment>;
  }
>(({ comment, postId, isReply = false, onReply, commentMap }, ref) => {
  const { commentId } = useGlobalSearchParams<{ commentId?: string }>();

  const isAnonymous = getCommentIsAnonymous(comment);

  const firstName = comment.user.firstName ?? '';

  const lastName = comment.user.lastName ?? '';

  const initials = isAnonymous ? 'A' : getInitials(firstName, lastName);

  return (
    <VStack className={twJoin(isReply ? 'ml-12' : 'rounded-lg')} id={`comment-${comment.id}`}>
      <ConversationEntry
        nested={isReply}
        highlighted={commentId === comment.id.toString()}
        avatar={
          <UserAvatar
            user={comment.user}
            initials={initials}
            isAnonymous={isAnonymous}
            anonymousAlias={comment.anonymousAlias}
          />
        }
        name={getDisplayName(firstName, lastName, isAnonymous, comment.anonymousAlias)}
        timeLabel={comment.createdAt ? getTimeAgo(comment.createdAt) : null}
        meta={
          <UserInfo
            userType={comment.user.userType}
            state={comment.user.state ?? undefined}
            isAnonymous={isAnonymous}
          />
        }
        body={
          <ConversationText mention={getReplyToName(comment, isReply, commentMap)}>
            {comment.body}
          </ConversationText>
        }
        footer={<CommentFooter comment={comment} postId={postId} onReply={onReply} />}
      />

      {'replies' in comment && comment.replies && comment.replies.length > 0 && (
        <>
          {comment.replies.map((reply) => (
            <VStack key={reply.id}>
              <Comment
                postId={postId}
                comment={reply}
                isReply
                onReply={onReply}
                commentMap={commentMap}
              />
            </VStack>
          ))}
        </>
      )}
    </VStack>
  );
});
